import { useEffect, useRef, useState } from 'react';
import { PageHero } from '@/components/brand/PageHero';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/Button';
import { projects, portfolioFilters, type PortfolioProject } from '@/content/portfolio';
import { usePageMeta } from '@/lib/usePageMeta';
import { getLenis } from '@/lib/useLenis';
import './portfolio.css';

export default function Portfolio() {
  usePageMeta('Software, AI & Website Portfolio', 'Explore selected websites, business platforms, and AI automation work by the Falcon Solutions team.', 'Portfolio');
  const [filter, setFilter] = useState<string>('All work');
  const [selected, setSelected] = useState<PortfolioProject | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const visible = projects.filter(project => filter === 'All work' || project.filter === filter);

  function closeProject() {
    setSelected(null);
    requestAnimationFrame(() => opener.current?.focus({ preventScroll: true }));
  }

  return (
    <>
      <PageHero compact label="Portfolio" headline="Ideas made tangible."
        description="Selected work by our team. Explore websites, business platforms, and AI workflows built around the people who use them."
        action="Explore the work" target="#projects" note="Including independent projects by members of our team." />

      <Section id="projects">
        <div className="portfolio-toolbar">
          <h2 className="text-display-md">Selected work</h2>
        </div>
        <div className="portfolio-filters" role="group" aria-label="Filter portfolio">
          {portfolioFilters.map(item => (
            <button key={item} type="button" aria-pressed={filter === item} onClick={() => setFilter(item)}>{item}</button>
          ))}
        </div>
        <div className="portfolio-grid">
          {visible.map((project, index) => (
            <article key={project.slug} className={`portfolio-card ${index === 0 && filter === 'All work' ? 'portfolio-featured' : ''}`}>
              <button type="button" className="portfolio-open" aria-label={`View ${project.title}`}
                onClick={event => { opener.current = event.currentTarget; setSelected(project); }}>
                <div className="portfolio-cover">
                  <img src={project.images[0].src} alt={project.images[0].alt} width={1448} height={1086}
                    loading={index === 0 ? 'eager' : 'lazy'} decoding="async" />
                </div>
                <div className="portfolio-card-copy">
                  <p className="portfolio-category">{project.category}</p>
                  <h3>{project.title}</h3>
                  <p className="portfolio-summary">{project.summary}</p>
                  <span className="portfolio-view">{project.kind === 'Collection' ? 'Explore collection' : 'View project'} <span aria-hidden="true">↗</span></span>
                </div>
              </button>
            </article>
          ))}
        </div>
      </Section>

      <Section tone="blue" size="sm">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div><h2 className="text-display-md">What are you building?</h2><p className="mt-4 max-w-[52ch] text-ink/70">Tell us about the problem you want to solve. We can help shape the next step.</p></div>
          <Button href="/contact" variant="brand">Discuss your project</Button>
        </div>
      </Section>
      {selected && <ProjectViewer project={selected} onClose={closeProject} />}
    </>
  );
}

function ProjectViewer({ project, onClose }: { project: PortfolioProject; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [index, setIndex] = useState(0);
  const image = project.images[index];
  const changeImage = (step: number) => setIndex(current => (current + step + project.images.length) % project.images.length);

  useEffect(() => {
    const node = dialog.current;
    const overflow = document.body.style.overflow;
    const lenis = getLenis();
    node?.showModal();
    lenis?.stop();
    document.body.style.overflow = 'hidden';
    return () => {
      node?.close();
      document.body.style.overflow = overflow;
      lenis?.start();
    };
  }, []);

  return (
    <dialog ref={dialog} className="portfolio-dialog" aria-labelledby="project-title" data-lenis-prevent
      onCancel={event => { event.preventDefault(); onClose(); }}
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}
      onKeyDown={event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); changeImage(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }}>
      <div className="portfolio-dialog-inner">
        <header className="portfolio-dialog-header">
          <div><p className="portfolio-category">{project.category}</p><h2 id="project-title">{project.title}</h2></div>
          <button autoFocus type="button" className="portfolio-close" onClick={onClose} aria-label="Close project">Close <span aria-hidden="true">×</span></button>
        </header>
        <div className="portfolio-detail-grid">
          <div className="portfolio-gallery">
            <img className="portfolio-full-image" src={image.src} alt={image.alt} width={1448} height={1086} />
            <div className="portfolio-gallery-controls">
              <button type="button" onClick={() => changeImage(-1)} disabled={project.images.length < 2} aria-label="Previous image">←</button>
              <p aria-live="polite">{index + 1} / {project.images.length}<span>{image.alt}</span></p>
              <button type="button" onClick={() => changeImage(1)} disabled={project.images.length < 2} aria-label="Next image">→</button>
            </div>
            {project.images.length > 1 && <div className="portfolio-thumbnails" role="group" aria-label="Project images">
              {project.images.map((item, i) => <button key={item.src} type="button" aria-label={`Show image ${i + 1}: ${item.alt}`} aria-pressed={index === i} onClick={() => setIndex(i)}>
                <img src={item.src} alt="" width={144} height={108} loading="lazy" />
              </button>)}
            </div>}
          </div>
          <div className="portfolio-description">
            <p className="portfolio-kind">{project.kind === 'Collection' ? 'Project collection' : 'Project overview'}</p>
            {project.description.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
            <h3>Areas of work</h3>
            <ul>{project.skills.map(skill => <li key={skill}>{skill}</li>)}</ul>
            <Button href={`/portfolio/${project.slug}`} variant="secondary" onClick={onClose}>Open full project page</Button>
          </div>
        </div>
      </div>
    </dialog>
  );
}
